'use strict';

define('vb/private/constants',['vbc/private/constants'], (CommonConstants) => {
  // copy over constants from CommonConstants
  const Constants = Object.assign({}, CommonConstants);

  /**
   * Supported Message Types allowed by JET.
   * @type {{ERROR: string, WARNING: string, INFO: string, CONFIRMATION: string, NONE: string}}
   * @see oj.Message component
   */
  Constants.MessageType = {
    ERROR: 'error',
    WARNING: 'warning',
    INFO: 'info',
    CONFIRMATION: 'confirmation',
    NONE: 'none',
  };

  /**
   * Supported variable name spaces.
   * - state (default): variable holds the state of a related object
   * - data: variable holds the data. example fetched from a backend service
   * - metadata: variable holds metadata. example: used with dynamic components
   * - builtin: builtin variable. Example currentPage and path.
   *
   * @type {{STATE: string, DATA: string, METADATA: string}}
   */
  Constants.VariableNamespace = {
    VARIABLES: 'variables',
    METADATA: 'metadata',
    BUILTIN: 'builtin',
    CONSTANTS: 'constants',
  };

  Constants.VariableClassification = {
    REGULAR: 'regular',
    INSTANCE_FACTORY: 'instanceFactory',
  };

  Constants.VariableTypePrefixes = {
    INSTANCE_FACTORY: 'vb/InstanceFactory',
  };

  /**
   * Suffixes appended to a builtin type variable.
   * @type {{VALUE: string, INTERNAL_STATE: string}}
   */
  Constants.BuiltinVariableName = {
    VALUE: '_value',
    INTERNAL_STATE: '_internalState',
  };

  /**
   * States a variable can be in its lifecycle.
   * - init: variable in the initialization stage. Usually the next state is active
   * - active: variable is live, meaning reading its state will yield a fully resolved value. Other variables it
   *    depends on, also resolve to their full value. Usually this is right before vbEnter
   * - dispose: variable is in the process of being destroyed / disposed
   * Note: This is especially so when other variables it depends on (might) have already been destroyed.
   * @type {{INIT: number, ACTIVE: number, DISPOSE: number}}
   */
  Constants.VariableLifecycleStage = {
    INIT: 1,
    ACTIVE: 2,
    DISPOSE: 4,
  };

  /**
   *
   * @type {{ALL: string, NONE: string}}
   */
  Constants.VariableWritablePropertyOptions = {
    ALL: 'all',
    NONE: 'none',
  };

  /**
   * Name of properties available in context object exposed to the user
   * @type {String}
   */
  Constants.ContextName = {
    PARAMETERS: '$parameters',
    EVENT: '$event',
    PREVIOUS: '$previous', // used in event listeners, for 'transform' behavior
    CURRENT: '$current',
    BINDING_CONTEXT: '$bindingContext',
    INIT_PARAMS: '$initParams',
  };

  // Used to prefix service endpoints (ex: “base:fooService/getPets”)
  Constants.ExtensionNamespaces = {
    BASE: 'base',
  };


  Constants.CatalogNamespaces = {
    BASE: Constants.ExtensionNamespaces.BASE,
    TENANT: 'tenant', // @todo: near-future use
  };


  // BASE is for files that extend files in the base app; SELF is for files that are new in the extension.
  Constants.ExtensionFolders = {
    BASE: 'base',
    SELF: 'self',
  };


  /**
   * The name of the property used to store the action results in the action scope results variable.
   * @type {String}
   */
  Constants.RESULTS_VARIABLE_KEY = 'vb_results';

  /**
   * Built-in variables
   * @type {String}
   */
  Constants.CURRENT_PAGE_VARIABLE = 'currentPage';
  Constants.CURRENT_FLOW_VARIABLE = 'currentFlow';
  Constants.LOCALE_VARIABLE = 'locale';
  Constants.PATH_VARIABLE = 'path';

  /**
   * built-in constants
   * @type {string}
   */
  Constants.PROFILE_CONSTANT = 'profile'; // $application.profile
  Constants.DEPLOYMENT_CONSTANT = 'deployment'; // $application.deployment

  // these are peers of PATH_VARIABLE/CURRENT_PAGE_VARIABLE because they appear in the availableContexts,
  // but are not Variable objects.
  Constants.RESPONSIVE_CONTEXT = 'responsive';
  Constants.TRANSLATIONS_CONTEXT = 'translations';
  Constants.COMPONENTS_CONTEXT = 'components';
  Constants.INFO_CONTEXT = 'info';
  Constants.APPLICATION_USER_VARIABLE = 'user';
  Constants.INIT_PARAM_CONTEXT = 'initParams';

  Constants.PARENT_FOLDER = `..${Constants.PATH_SEPARATOR}`;
  Constants.CURRENT_FOLDER = `.${Constants.PATH_SEPARATOR}`;
  Constants.RELATIVE_FOLDER_PREFIX = '.';

  Constants.APPLICATION_PREFIX = 'application';
  Constants.FLOW_PREFIX = 'flow';
  Constants.PAGE_PREFIX = 'page';
  Constants.LAYOUT_PREFIX = 'layout';
  Constants.APP_PACKAGE_PREFIX = 'package';
  Constants.THIS_PREFIX = 'this';

  /**
   * Page event names
   */
  Constants.VB_EVENT_MARKER = 'vb';
  Constants.ENTER_EVENT = 'vbEnter';
  Constants.EXIT_EVENT = 'vbExit';
  Constants.BEFORE_ENTER_EVENT = 'vbBeforeEnter';
  Constants.BEFORE_EXIT_EVENT = 'vbBeforeExit';
  Constants.INTERACTABLE_EVENT = 'vbInteractable';
  Constants.NOTIFICATION_EVENT = 'vbNotification';
  Constants.AFTER_NAVIGATE_EVENT = 'vbAfterNavigate';
  Constants.INSTALL_PROMPT_EVENT = 'vbBeforeAppInstallPrompt';
  Constants.RESUME_EVENT = 'vbResume';
  Constants.PAUSE_EVENT = 'vbPause';
  Constants.DATAPROVIDER_NOTIFICATION_EVENT = 'vbDataProviderNotification';
  Constants.RESOURCE_CHANGED_EVENT = 'vbResourceChanged';
  Constants.NEW_CONTENT_AVAILABLE = 'vbNewContentAvailable';

  /**
   * variable events
   */
  // the event listener set on variable is 'onValueChanged' but the event name, though not
  // explicitly declared anywhere, could follow other vb event names - prefixed by 'vb' but for
  // whatever reason we have diverged from convention!.
  Constants.VALUE_CHANGED = 'onValueChanged';

  Constants.ContainerState = {
    CREATED: 'created',
    ENTERED: 'entered',
    EXITED: 'exited',
    REFRESHING: 'refreshing',
  };

  Constants.RouterStrategy = { QUERY: 'query', PATH: 'path' };

  /**
   * Name of the function on the page viewModel use to retrieve the
   * ojModule configuration for a flow.
   * @type {String}
   */
  Constants.flowModuleConfigFunctionName = 'flowModuleConfig';
  Constants.appModuleConfig = 'vbApplication';
  Constants.routerModuleConfig = 'vbRouterFlow';

  /**
   * The configuration object for an empty ojModule
   * @type {Object}
   */
  Constants.blankModuleConfig = { view: [], viewModel: null };

  /**
   * A descriptor used for the error page
   */
  Constants.errorPageDescriptor = {
    pageModelVersion: '1.0.0',
    description: 'Error Page',
  };

  /**
   * Service constants
   * @type {{definitionTimeout: number}}
   */
  Constants.Services = {
    definitionTimeout: 30000, // number of milliseconds to wait for a service definition
  };

  /**
   * The default rate limit in milliseconds for limiting how often onValueChanged should be fired
   * @type {number}
   */
  Constants.DEFAULT_RATE_LIMIT = 1;

  /**
   * The maximum depth we would recursively create default values for cyclic types
   * @type {number}
   */
  Constants.MAX_DEFAULT_VALUE_RECURSION_DEPTH = 1;

  /**
   * mime types
   * @type {{MULTIPART: string}}
   */
  Constants.ContentTypes = {
    MULTIPART: 'multipart/form-data',
  };

  Constants.Decorators = {
    PREFIX: '@decorators',
  };

  Constants.DefaultPaths = {
    LAYOUTS: 'dynamicLayouts/',
    CATALOG_JSON: 'services/catalog.json',
  };

  Constants.EXTENSION_PATH_NAME = 'vx';
  Constants.EXTENSION_PATH = `${Constants.EXTENSION_PATH_NAME}${Constants.PATH_SEPARATOR}`;

  Constants.EventTargets = {
    // the container which contains the chain the owns the action
    CURRENT: 'current',
    // the 'lowest' container in the current hierarchy (typically a Page).
    LEAF: 'leaf',
  };

  // declared event behaviors
  Constants.EventBehaviors = {
    NOTIFY: 'notify',
    NOTIFY_WAIT: 'notifyAndWait',
    CHECK_CANCEL: 'checkForCancel',
    TRANSFORM: 'transform',
    DYNAMIC_COMP: 'dynamicComponent', // a special behavior; used for dynamic UI template events
  };

  // declared event mode
  Constants.EventMode = {
    LISTENABLE: 'listenable',
    TRIGGERABLE: 'triggerable',
  };

  // returns from event listener processing, to differentiate from 'no return value'
  Constants.NO_EVENT_LISTENER_RESPONSE = '__no_listener__';

  Constants.User = {
    DEFAULT: 'anonymous',
    TESTER: 'tester',
  };

  /**
   *
   * @type {{ACTION_CHAIN: string, WEBDRIVER: string}}
   */
  Constants.TestMode = {
    ACTION_CHAIN: 'actionChain',
    WEBDRIVER: 'webdriver',
  };

  /**
   * An array of all possible scopes used in expressions
   * @type {Array<String>}
   */
  Constants.ALL_SCOPES = ['$application', '$package', '$flow', '$page', '$base', '$extension', '$chain', '$variables',
    '$layout'];

  return Constants;
});

